import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { payloadAPI } from '@/lib/payload'
import { useCallback } from 'react'
import { useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'

export function useAuth() {
  const queryClient = useQueryClient()
  const router = useRouter()

  const { data: user, isLoading } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: () => payloadAPI.getMe(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      payloadAPI.login(email, password),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'user'], data.user)
      localStorage.setItem('token', data.token)
      router.navigate({ to: '/dashboard' })
      toast.success('Welcome back!')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Login failed')
    },
  })

  const registerMutation = useMutation({
    mutationFn: (data: {
      email: string
      password: string
      name: string
      tradingExperience?: string
      tradingStyle?: string
    }) => payloadAPI.register(data),
    onSuccess: () => {
      toast.success('Account created! Please log in.')
      router.navigate({ to: '/login' })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Registration failed')
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => payloadAPI.logout(),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'user'], null)
      localStorage.removeItem('token')
      router.navigate({ to: '/' })
      toast.success('Logged out successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Logout failed')
    },
  })

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => payloadAPI.forgotPassword(email),
    onSuccess: () => {
      toast.success('Password reset email sent')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send reset email')
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      payloadAPI.resetPassword(token, password),
    onSuccess: () => {
      toast.success('Password reset successful')
      router.navigate({ to: '/login' })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Password reset failed')
    },
  })

  const login = useCallback(
    (email: string, password: string) => {
      loginMutation.mutate({ email, password })
    },
    [loginMutation]
  )

  const register = useCallback(
    (data: {
      email: string
      password: string
      name: string
      tradingExperience?: string
      tradingStyle?: string
    }) => {
      registerMutation.mutate(data)
    },
    [registerMutation]
  )

  const logout = useCallback(() => {
    logoutMutation.mutate()
  }, [logoutMutation])

  const forgotPassword = useCallback(
    (email: string) => {
      forgotPasswordMutation.mutate(email)
    },
    [forgotPasswordMutation]
  )

  const resetPassword = useCallback(
    (token: string, password: string) => {
      resetPasswordMutation.mutate({ token, password })
    },
    [resetPasswordMutation]
  )

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isDeveloper: user?.role === 'developer',
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  }
}